import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Table, Alert, Row, Col, Spinner, Badge, ListGroup } from "react-bootstrap";
import axios from "axios";
import config from "../../config";
import { toast } from "react-toastify";
import DocumentUpload from "../common/DocumentUpload";

export default function ViewEditDispatchModal({ show, onHide, dispatchId }) {
  const [dispatchNote, setDispatchNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form fields
  const [dispatchDate, setDispatchDate] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverContact, setDriverContact] = useState("");
  const [transporter, setTransporter] =useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [documents, setDocuments] = useState([]);
  const [newDocuments, setNewDocuments] = useState([]);
  const [deletedDocuments, setDeletedDocuments] = useState([]);

  // New state variables for selectors
  const [selectedTransporterId, setSelectedTransporterId] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [transportersList, setTransportersList] = useState([]);
  const [vehiclesList, setVehiclesList] = useState([]);
  const [driversList, setDriversList] = useState([]);

  // Loading states for dropdowns
  const [loadingTransporters, setLoadingTransporters] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  // Fetch Transporters
  const fetchTransportersList = useCallback(async () => {
    setLoadingTransporters(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/transporters`);
      setTransportersList(res.data || []);
    } catch (err) {
      console.error("Error fetching transporters:", err);
      toast.error("Failed to fetch transporters");
    } finally {
      setLoadingTransporters(false);
    }
  }, []);

  // Fetch Vehicles by Transporter
  const fetchVehiclesList = useCallback(async (transporterId) => {
    if (!transporterId) {
      setVehiclesList([]);
      return;
    }
    setLoadingVehicles(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/vehicles/by-transporter/${transporterId}`);
      setVehiclesList(res.data || []);
    } catch (err) {
      console.error("Error fetching vehicles:", err);
      toast.error("Failed to fetch vehicles for the selected transporter");
    } finally {
      setLoadingVehicles(false);
    }
  }, []);

  // Fetch Drivers by Transporter
  const fetchDriversList = useCallback(async (transporterId) => {
    if (!transporterId) {
      setDriversList([]);
      return;
    }
    setLoadingDrivers(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/drivers/by-transporter/${transporterId}`);
      setDriversList(res.data || []);
    } catch (err) {
      console.error("Error fetching drivers:", err);
      toast.error("Failed to fetch drivers for the selected transporter");
    } finally {
      setLoadingDrivers(false);
    }
  }, []);


  useEffect(() => {
    if (show && dispatchId) {
      fetchTransportersList(); // Fetch all transporters when modal opens
      fetchDispatchNote();
    }
  }, [show, dispatchId, fetchTransportersList, fetchVehiclesList, fetchDriversList]);

  // Effect to fetch vehicles and drivers when transporter changes in editing mode
  useEffect(() => {
    if (isEditing && selectedTransporterId) {
      fetchVehiclesList(selectedTransporterId);
      fetchDriversList(selectedTransporterId);
    } else if (isEditing && !selectedTransporterId) {
      setVehiclesList([]);
      setDriversList([]);
      setSelectedVehicleId("");
      setSelectedDriverId("");
      setVehicleNumber("");
      setDriverName("");
      setDriverContact("");
    }
  }, [isEditing, selectedTransporterId, fetchVehiclesList, fetchDriversList]);

  const fetchDispatchNote = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/dispatches/${dispatchId}`);
      const dispatch = res.data;
      setDispatchNote(dispatch);
      // Initialize form fields
      setDispatchDate(dispatch.dispatchDate.split('T')[0]);
      setVehicleNumber(dispatch.vehicleNumber || "");
      setDriverName(dispatch.driverName || "");
      setDriverContact(dispatch.driverContact || "");
      setTransporter(dispatch.transporter || ""); // Snapshot field
      setExpectedDeliveryDate(dispatch.expectedDeliveryDate ? dispatch.expectedDeliveryDate.split('T')[0] : "");
      setRemarks(dispatch.remarks || "");
      setDocuments(dispatch.documents || []);
      setNewDocuments([]);
      setDeletedDocuments([]);

      // Initialize selected IDs for dropdowns
      setSelectedTransporterId(dispatch.transporterId || "");
      setSelectedVehicleId(dispatch.vehicleId || "");
      setSelectedDriverId(dispatch.driverId || "");

      // If transporter is set, fetch vehicles and drivers for it
      if (dispatch.transporterId) {
        fetchVehiclesList(dispatch.transporterId);
        fetchDriversList(dispatch.transporterId);
      }
    } catch (err) {
      setError("Failed to fetch dispatch note details.");
      toast.error("Failed to fetch dispatch note details.");
      console.error(err);
    }
    finally {
      setLoading(false);
    }
  }, [dispatchId, fetchVehiclesList, fetchDriversList]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleClose = () => {
    setIsEditing(false);
    setDispatchNote(null);
    setNewDocuments([]);
    onHide();
  };

  const handleAddDocument = () => {
    setNewDocuments([...newDocuments, { documentType: '', file: null, fileName: '' }]);
  };

  const handleRemoveNewDocument = (index) => {
    const updated = newDocuments.filter((_, i) => i !== index);
    setNewDocuments(updated);
  };
  
  const handleNewDocumentChange = (index, data) => {
    const updated = [...newDocuments];
    updated[index] = data;
    setNewDocuments(updated);
  };

  const handleExistingDocumentChange = (index, data) => {
    const updated = [...documents];
    updated[index] = data;
    setDocuments(updated);
  };

  const handleRemoveExistingDocument = (index) => {
    const docToRemove = documents[index];
    if (docToRemove._id) {
      setDeletedDocuments([...deletedDocuments, docToRemove._id]);
    }
    const updated = documents.filter((_, i) => i !== index);
    setDocuments(updated);
  };
  

  const handleSaveChanges = async () => {
    setLoading(true);
    try {
        const formData = new FormData();
        formData.append('dispatchDate', dispatchDate);
        formData.append('transporterId', selectedTransporterId || '');
        formData.append('vehicleId', selectedVehicleId || '');
        formData.append('driverId', selectedDriverId || '');
        formData.append('vehicleNumber', vehicleNumber || '');
        formData.append('driverName', driverName || '');
        formData.append('driverContact', driverContact || '');
        formData.append('transporter', transporter || '');
        formData.append('expectedDeliveryDate', expectedDeliveryDate);
        formData.append('remarks', remarks);

        const allDocuments = [...documents, ...newDocuments];
        const metadata = [];
        
        allDocuments.forEach(doc => {
            const { file, ...meta } = doc;
            metadata.push(meta);
            if (file) {
                formData.append('documents', file);
            }
        });

        formData.append('documents_meta', JSON.stringify(metadata));

        deletedDocuments.forEach((docId) => {
          formData.append('deletedDocuments[]', docId);
        });

        await axios.put(`${config.API_BASE_URL}/dispatches/${dispatchId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        
        toast.success("Dispatch note updated successfully.");
        fetchDispatchNote(); // Refresh data
        setIsEditing(false);
    } catch (err) {
        toast.error(err.response?.data?.message || "Failed to save changes.");
        console.error(err);
    } finally {
        setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Dispatch Note: {dispatchNote?.dispatchNumber}
          {dispatchNote?.status && <Badge bg="info" className="ms-2">{dispatchNote.status}</Badge>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className="text-center"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {dispatchNote && (
          <Form>
            <Row>
              <Col md={4}><Form.Group className="mb-3">
                <Form.Label>Dispatch Date</Form.Label>
                <Form.Control type="date" value={dispatchDate} onChange={e => setDispatchDate(e.target.value)} readOnly={!isEditing} />
              </Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3">
                <Form.Label>Expected Delivery Date</Form.Label>
                <Form.Control type="date" value={expectedDeliveryDate} onChange={e => setExpectedDeliveryDate(e.target.value)} readOnly={!isEditing} />
              </Form.Group></Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Transporter</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      value={selectedTransporterId}
                      onChange={(e) => {
                        const newTransporterId = e.target.value;
                        setSelectedTransporterId(newTransporterId);
                        // Reset vehicle and driver when transporter changes
                        setSelectedVehicleId("");
                        setSelectedDriverId("");
                        setVehicleNumber("");
                        setDriverName("");
                        setDriverContact("");
                        setVehiclesList([]);
                        setDriversList([]);
                        // Update transporter snapshot name
                        const selectedT = transportersList.find(t => t._id === newTransporterId);
                        setTransporter(selectedT ? selectedT.name : "");
                        if (newTransporterId) {
                          fetchVehiclesList(newTransporterId);
                          fetchDriversList(newTransporterId);
                        }
                      }}
                      disabled={!isEditing || loadingTransporters}
                    >
                      <option value="">{loadingTransporters ? 'Loading...' : '-- Select Transporter --'}</option>
                      {transportersList.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                    </Form.Select>
                  ) : (
                    <Form.Control type="text" value={dispatchNote?.transporter || ""} readOnly />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Number</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      value={selectedVehicleId}
                      onChange={(e) => {
                        const newVehicleId = e.target.value;
                        setSelectedVehicleId(newVehicleId);
                        const selectedV = vehiclesList.find(v => v._id === newVehicleId);
                        setVehicleNumber(selectedV ? selectedV.vehicleNumber : "");
                      }}
                      disabled={!isEditing || !selectedTransporterId || loadingVehicles}
                    >
                      <option value="">{loadingVehicles ? 'Loading...' : '-- Select Vehicle --'}</option>
                      {vehiclesList.map(v => <option key={v._id} value={v._id}>{v.vehicleNumber}</option>)}
                    </Form.Select>
                  ) : (
                    <Form.Control type="text" value={dispatchNote?.vehicleNumber || ""} readOnly />
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Driver Name</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      value={selectedDriverId}
                      onChange={(e) => {
                        const newDriverId = e.target.value;
                        setSelectedDriverId(newDriverId);
                        const selectedD = driversList.find(d => d._id === newDriverId);
                        setDriverName(selectedD ? selectedD.name : "");
                        setDriverContact(selectedD ? selectedD.phone : "");
                      }}
                      disabled={!isEditing || !selectedTransporterId || loadingDrivers}
                    >
                      <option value="">{loadingDrivers ? 'Loading...' : '-- Select Driver --'}</option>
                      {driversList.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                    </Form.Select>
                  ) : (
                    <Form.Control type="text" value={dispatchNote?.driverName || ""} readOnly />
                  )}
                </Form.Group>
              </Col>
              <Col md={4}><Form.Group className="mb-3">
                <Form.Label>Driver Contact</Form.Label>
                <Form.Control type="text" value={driverContact} onChange={e => setDriverContact(e.target.value)} readOnly={!isEditing} />
              </Form.Group></Col>
            </Row>

            <h6 className="mt-3">Dispatched Items</h6>
            <Table bordered striped responsive size="sm">
                <thead>
                    <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Inner Packing</th>
                        <th>Inner Bags</th>
                        <th>Outer Packing</th>
                        <th>Outer Bags</th>
                        <th>Bags/Outer</th>
                        <th>Warehouse</th>
                    </tr>
                </thead>
                <tbody>
                    {dispatchNote.items.map(item => (
                        <tr key={item._id}>
                            <td>{item.product?.productName || 'N/A'}</td>
                            <td>{item.quantityDispatched} {item.unit}</td>
                            <td>{item.packingMaterialId?.name || 'N/A'}</td>
                            <td>{item.numberOfBags || 'N/A'}</td>
                            <td>{item.outerPackingMaterialId?.name || 'N/A'}</td>
                            <td>{item.outerNumberOfBags || 'N/A'}</td>
                            <td>{item.bagsPerOuterBag?.toFixed(2) || 'N/A'}</td>
                            <td>{item.warehouseLocationId?.name || 'N/A'}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <h6 className="mt-4">Documents</h6>
            {isEditing ? (
              <>
                {documents.map((doc, index) => (
                  <DocumentUpload
                    key={doc._id || `new-${index}`}
                    index={index}
                    document={doc}
                    onDocumentChange={handleExistingDocumentChange}
                    onRemoveDocument={() => handleRemoveExistingDocument(index)}
                  />
                ))}
                <h6 className="mt-4">Upload New Documents</h6>
                {newDocuments.map((doc, index) => (
                    <DocumentUpload
                        key={`new-doc-${index}`}
                        index={index}
                        onDocumentChange={handleNewDocumentChange}
                        onRemoveDocument={() => handleRemoveNewDocument(index)}
                    />
                ))}
                <Button variant="secondary" size="sm" onClick={handleAddDocument}>
                    + Add Document
                </Button>
              </>
            ) : (
              <ListGroup className="mb-3">
                {documents.map((doc) => (
                  <ListGroup.Item key={doc._id} className="d-flex justify-content-between align-items-center">
                    <div>
                      <a href={`${config.DOC_BASE_URL}/uploads/${doc.filePath}`} target="_blank" rel="noopener noreferrer">
                        {doc.fileName || doc.documentType}
                      </a>
                      <small className="text-muted ms-2">({doc.documentType})</small>
                    </div>
                  </ListGroup.Item>
                ))}
                {documents.length === 0 && <ListGroup.Item>No documents uploaded.</ListGroup.Item>}
              </ListGroup>
            )}
            
            <Form.Group className="mb-3 mt-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control as="textarea" rows={3} value={remarks} onChange={e => setRemarks(e.target.value)} readOnly={!isEditing} />
            </Form.Group>

          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        {isEditing ? (
          <Button variant="success" onClick={handleSaveChanges} disabled={loading}>
            {loading ? <Spinner as="span" animation="border" size="sm" /> : "Save Changes"}
          </Button>
        ) : (
          <Button variant="primary" onClick={handleEditToggle}>
            Edit
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
